<script setup>
import SitemapTable from "@/components/sitemap/includes/SitemapTable.vue";
import {useStore} from "vuex";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import {computed, ref} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import SweetToast from "@/SweetToast";
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import useUser from "@/composables/useUser";
import useCompleteOnboarding from "@/composables/ai-onboarding/useCompleteOnboarding";
import PagesFeedbackModal from "@/components/ai-onboarding/modals/PagesFeedbackModal.vue";
import useKit from "@/composables/useKit";

const store = useStore();
const showFeedbackModal = ref(false);

const strategyId = computed(() => store.getters['strategy/strategyId']);
const emit = defineEmits(['changeStep']);
const {hasAccessToOptimiser} = useUser();

const {completeOnboardingMutation, isCompletingOnboarding} = useCompleteOnboarding();

const {mutate: submitSitemap, isPending} = useMutation({
  mutationFn: (strategyId) => {
    store.commit('strategy/SET_PROCESSING', false);
    return axios.post('/strategy/save-sitemap', {
      strategyId,
      recrawl: false,
    });
  },
  onSuccess: ({data}) => {
    SweetToast.fire({
      icon: "success",
      title: 'Sitemap saved successfully',
    })
    // Update the vuex store for the sitemap table component
    store.commit('strategy/SET_PROCESSING', false);
    store.commit('strategy/SET_STRATEGY', data.strategy);

    if (hasAccessToOptimiser.value) {
      changeStep(Steps.value.ONSITE_OPTIMIZER, emit);
      return
    }

    completeOnboardingMutation.mutate();
  },
  onError: ({response}) => {
    store.commit('strategy/SET_PROCESSING', false);
    if (response.status === 400) {
      SweetToast.fire({
        icon: 'error',
        title: response.data.message,
      });
    }

    throw new Error('Something went wrong');
  },
});

const {canSeeKit} = useKit();

const isProcessing = computed(() => isPending.value || isCompletingOnboarding.value);

const handleContinue = async () => {
  // we don't show the modal to users who cant see Kit.
  if (!canSeeKit.value) {
    submitSitemap(strategyId.value);
    return;
  }

  showFeedbackModal.value = true;
}

const handleFeedbackSubmitted = () => {
  showFeedbackModal.value = false;
  submitSitemap(strategyId.value);
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-text-center tw-p-18 tw-overflow-y-auto">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-6">
        <div class="tw-flex tw-flex-col tw-w-full tw-text-grey-dark">
          <SitemapTable is-onboarding />
        </div>
      </div>
    </div>
    <NavigationButtons
      :loading="isProcessing"
      :show-back-button="false"
      @next="handleContinue"
    />

    <PagesFeedbackModal
      :show-modal="showFeedbackModal"
      @submitted="handleFeedbackSubmitted"
    />
  </div>
</template>
